import React, { FC, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch } from 'react-redux';
import { setModalShowing } from 'actions/gallery';
import { Portal } from '../Portal/index';
import Typography from '../Typography';
import ButtonBar from '../Button/ButtonBar';
import FontIcon from '../FontIcon';

const Backdrop = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--sys-color--shadow);
  opacity: 0.4;
  z-index: 65536;
  pointer-events: ${({ show }) => (show ? 'all' : 'none')};
`;

const DialogContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 85%;
  max-width: 328px;
  background-color: var(--sys-color--surface-container-high);
  border-radius: 28px;
  z-index: 65537;
  display: flex;
  flex-direction: column;
  padding: 24px;
  box-sizing: border-box;
  max-height: calc(100vh - 48px);
`;

const Header = styled.div<{ $center?: boolean }>`
  margin-bottom: 16px;
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
`;

const HeroIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  color: var(--sys-color--secondary);
`;

const ContentArea = styled.div`
  color: var(--sys-color--on-surface-variant);
  margin-bottom: 24px;
  flex-grow: 1;
  overflow-y: auto;

  /* Hide scrollbars but keep scrolling active */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--sys-color--outline-variant);
    border-radius: 4px;
  }
`;

const ActionsArea = styled.div<{ layout?: 'vertical' | 'horizontal' }>`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;

  ${({ layout }) =>
    layout === 'vertical'
      ? css`
          flex-direction: column-reverse;
          align-items: stretch;
          width: 100%;
          gap: 12px;
        `
      : css`
          flex-direction: row;
          flex-wrap: wrap;
        `}
`;

export interface DialogAction {
  text: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: 'filled' | 'outline' | 'text';
  id?: string;
  disabled?: boolean;
}

export interface DialogProps {
  show: boolean;
  title?: string | React.ReactNode;
  message?: string | React.ReactNode;
  children?: React.ReactNode;
  actions?: DialogAction[];
  onDismiss?: () => void;
  layout?: 'vertical' | 'horizontal';
  id?: string;
  heroIcon?: string;
}

export const Dialog: FC<DialogProps> = ({
  show,
  title,
  message,
  children,
  actions = [],
  onDismiss,
  layout,
  id,
  heroIcon,
}) => {
  const dispatch = useDispatch();
  const dialogId = useMemo(() => id || `dialog-${Math.random().toString(36).substr(2, 9)}`, [id]);

  // Handle modal scroll lock via redux
  useEffect(() => {
    if (show) {
      dispatch(setModalShowing(true));
    } else {
      dispatch(setModalShowing(false));
    }
    return () => {
      dispatch(setModalShowing(false));
    };
  }, [show, dispatch]);

  if (!show) return null;

  // Determine default actions layout
  // Stack vertically if 2+ actions by default, unless explicitly overridden
  const finalLayout = layout || (actions.length > 2 ? 'vertical' : 'horizontal');

  return (
    <Portal
      id={dialogId}
      content={
        <>
          <Backdrop show={show} onClick={onDismiss} />
          <DialogContainer role="dialog" aria-modal="true" id={`${dialogId}-container`}>
            {heroIcon && (
              <HeroIconWrapper>
                <FontIcon name={heroIcon} size={24} color="var(--sys-color--secondary)" />
              </HeroIconWrapper>
            )}
            {title && (
              <Header $center={!!heroIcon}>
                {typeof title === 'string' ? (
                  <Typography variant="headlineSmallEmphasized" color="var(--sys-color--on-surface)">
                    {title}
                  </Typography>
                ) : (
                  title
                )}
              </Header>
            )}
            <ContentArea>
              {children ? (
                children
              ) : typeof message === 'string' ? (
                <Typography variant="bodyMedium" color="var(--sys-color--on-surface-variant)">
                  {message}
                </Typography>
              ) : (
                message
              )}
            </ContentArea>
            {actions.length > 0 && (
              <ActionsArea layout={finalLayout}>
                {actions.map((action, index) => {
                  return (
                    <ButtonBar
                      key={index}
                      variant={action.variant || 'text'}
                      compact={true}
                      onClick={action.onClick}
                      id={action.id}
                      aria-label={action.id}
                      fullWidth={finalLayout === 'vertical'}
                      disabled={action.disabled}
                    >
                      {action.text}
                    </ButtonBar>
                  );
                })}
              </ActionsArea>
            )}
          </DialogContainer>
        </>
      }
    />
  );
};

export default Dialog;
